import { FilterPopoverTabs } from '@/Enums/Advertisers/FilterPopoverTabs.enum';
import { IClientControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { ClientsTabButton, WhitelabelsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/advertisers';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { isRoleAllowedWhitelabelSelect } from '@/helpers/roles';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectAdvertisersFilters } from '@/store/selectors/advertisers.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectRibeyeAllClients,
  selectRibeyeAllWhitelabels,
  selectSharedApiLoading,
} from '@/store/selectors/sharedApi.selectors';
import { setFilters } from '@/store/slices/advertisers.slice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEvent, SyntheticEvent, useCallback, useMemo, useState } from 'react';

const FilterPopover = () => {
  const dispatch = useAppDispatch();

  const meRole = useAppSelector(selectMeRole);
  const clients = useAppSelector(selectRibeyeAllClients);
  const whitelabels = useAppSelector(selectRibeyeAllWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);
  const filters = useAppSelector(selectAdvertisersFilters);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pendingClients, setPendingClients] = useState<IClientControlOption[]>(filters.clients);
  const [pendingWhitelabels, setPendingWhitelabels] = useState<IWhitelabelControlOption[]>(filters.whitelabels);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Clients);

  const showWhitelabelLevel = useMemo<boolean>(() => isRoleAllowedWhitelabelSelect(meRole), [meRole]);
  const filtersOpen = useMemo<boolean>(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const FILTER_TABS = useMemo(
    () =>
      showWhitelabelLevel ? [FilterPopoverTabs.Whitelabels, FilterPopoverTabs.Clients] : [FilterPopoverTabs.Clients],
    [showWhitelabelLevel],
  );

  const options = useMemo(() => {
    return getFilterOptions({
      userRole: meRole!,
      clients,
      selectedClients: pendingClients,
      selectedWhitelabels: pendingWhitelabels,
      whitelabels,
    });
  }, [clients, meRole, pendingClients, pendingWhitelabels, whitelabels]);

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const resetPendingValues = useCallback(() => {
    setPendingWhitelabels(filters.whitelabels);
    setPendingClients(filters.clients);
  }, [filters.clients, filters.whitelabels]);

  const updateWhitelabelsValue = useCallback(
    (updatedWhitelabels: IWhitelabelControlOption[] = []) => {
      setPendingWhitelabels(updatedWhitelabels);
      setPendingClients([]);
    },
    [setPendingWhitelabels, setPendingClients],
  );

  const updateClientsValue = useCallback(
    (updatedClients: IClientControlOption[] = []) => {
      setPendingClients(updatedClients);
    },
    [setPendingClients],
  );

  const handleOpen = useCallback(
    ({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(() => {
    setFilterAnchorEl(null);
    resetPendingValues();
    setCurrentTab(FILTER_TABS[0]);
  }, [FILTER_TABS, resetPendingValues, setCurrentTab, setFilterAnchorEl]);

  const handleTabChange = useCallback((_: SyntheticEvent, newValue: FilterPopoverTabs) => {
    setCurrentTab(newValue);
  }, []);

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filters.whitelabels, pendingWhitelabels) &&
      areArraysOfObjectsEqual(filters.clients, pendingClients)
    ) {
      handleClose();
      return;
    }
    dispatch(
      setFilters({
        whitelabels: pendingWhitelabels,
        clients: pendingClients,
      }),
    );
    handleClose();
  }, [filters.whitelabels, filters.clients, handleClose, pendingClients, pendingWhitelabels, dispatch]);

  return (
    <>
      <AddFilterButton onClick={handleOpen} describedBy={filtersId} variant='outlined' />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='pacing filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          {showWhitelabelLevel ? (
            <Tab
              value={FilterPopoverTabs.Whitelabels}
              component={WhitelabelsTabButton}
              {...a11yProps(FilterPopoverTabs.Whitelabels, BASE_TAB_ID)}
            />
          ) : null}
          <Tab
            value={FilterPopoverTabs.Clients}
            component={ClientsTabButton}
            {...a11yProps(FilterPopoverTabs.Clients, BASE_TAB_ID)}
          />
        </Tabs>
        {showWhitelabelLevel ? (
          <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Whitelabels}>
            <TabSelect
              label='Whitelabels'
              loading={loadingFilterSource}
              options={options.whitelabel}
              value={pendingWhitelabels}
              onChange={updateWhitelabelsValue}
              onClose={handleClose}
              onSubmit={handleSubmit}
            />
          </TabPanel>
        ) : null}
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Clients}>
          <TabSelect
            label='Client'
            loading={loadingFilterSource}
            options={options.client}
            value={pendingClients}
            onChange={updateClientsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
